import React, { useState, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLink,
  faShieldAlt,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faInfoCircle,
  faChevronDown,
  faShareAlt,
  faArrowLeft,
  faChartBar,
  faExchangeAlt,
  faEraser,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import "../css/URLSafetyAnalysis.css";
import "../../css/Common.css";
import "../text/css/SmsAnalysisSimpleMobile.css";
import SuggestionsBulletList from "../common/SuggestionsBulletList";
import InlineShareForm from "../common/InlineShareForm";
import useShareAlert from "../common/useShareAlert";
import { useSubscription } from "../../context/SubscriptionContext";
import { useTranslation } from "../../../../i18n/LanguageContext";
import DisclosuresModal from "../common/DisclosuresModal";
import HelpGuideButton from "../../HelpGuideButton";

const URLSafetyAnalysisMobile = ({
  url,
  setUrl,
  context,
  setContext,
  analysisResult,
  error,
  reportId,
  handleSubmit,
  handleClear,
  setHeroContent,
}) => {
  const { t } = useTranslation();
  const share = useShareAlert();
  const { canShare } = useSubscription();
  const isIndividualPlan = !canShare();

  const [activeSlide, setActiveSlide] = useState("input");
  const mobileContainerRef = useRef(null);

  // Auto-advance to results when analysis completes
  const prevResultRef = useRef(analysisResult);
  if (analysisResult && analysisResult !== prevResultRef.current) {
    prevResultRef.current = analysisResult;
    if (activeSlide === "input") {
      setTimeout(() => setActiveSlide("results"), 100);
    }
  }

  const scrollMobileTop = useCallback(() => {
    if (mobileContainerRef.current) {
      mobileContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const showInputSlide = useCallback(() => {
    setActiveSlide("input");
    scrollMobileTop();
  }, [scrollMobileTop]);

  const showResultsSlide = useCallback(() => {
    setActiveSlide("results");
    scrollMobileTop();
  }, [scrollMobileTop]);

  const getThreatBannerClass = () => {
    if (analysisResult?.isScam === true) return "threat-detected";
    if (analysisResult?.isScam === false) return "safe";
    return "security-failed";
  };

  const getThreatIcon = () => {
    if (analysisResult?.isScam === true) return faTimesCircle;
    if (analysisResult?.isScam === false) return faCheckCircle;
    return faExclamationTriangle;
  };

  const renderInputForm = () => (
    <div className="url-input-section">
      <form onSubmit={handleSubmit} className="url-form">
        <div className="form-group">
          <label htmlFor="urlsa-mobile-url-input">
            <FontAwesomeIcon icon={faLink} className="label-icon" />
            {t("urlsa.form.urlLabel")}
          </label>
          <div className="input-wrapper">
            <div className="input-with-clear">
              <input
                type="text"
                id="urlsa-mobile-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t("urlsa.form.urlPlaceholder")}
                className="url-input"
                autoComplete="off"
                required
              />
              {url && (
                <button
                  type="button"
                  className="clear-input-button"
                  onClick={() => setUrl("")}
                  aria-label={t("urlsa.form.clearAriaLabel")}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="urlsa-mobile-context-input">
            <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
            {t("urlsa.form.contextLabel")}
          </label>
          <textarea
            id="urlsa-mobile-context-input"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={t("urlsa.form.contextPlaceholder")}
            className="additional-context-textarea"
            rows={3}
          />
        </div>
        <div className="button-row">
          <button type="submit" className="analyze-button">
            <FontAwesomeIcon icon={faShieldAlt} className="button-icon" />
            {t("urlsa.form.analyzeBtn")}
          </button>
          {(url || context || analysisResult || error) && (
            <button
              type="button"
              className="clear-all-button"
              onClick={handleClear}
            >
              <FontAwesomeIcon icon={faEraser} className="button-icon" />
              {t("urlsa.form.clearBtn")}
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {error}
        </div>
      )}

      {analysisResult && (
        <button
          type="button"
          className="urlsa-view-results-link"
          onClick={showResultsSlide}
        >
          <FontAwesomeIcon icon={faChartBar} />
          {t("urlsa.form.viewResults")}
        </button>
      )}
    </div>
  );

  const renderResultsContent = () => {
    if (!analysisResult) return null;

    return (
      <div className="analysis-results">
        {analysisResult.wasRedirect && (
          <details className="redirect-notice">
            <summary className="redirect-notice-summary">
              <div className="redirect-notice-icon">
                <FontAwesomeIcon icon={faExchangeAlt} />
              </div>
              <span className="redirect-notice-title">
                <strong>{t("urlsa.redirect.detected")}</strong>{" "}
                {t("urlsa.redirect.summary").replace(
                  "{platform}",
                  analysisResult.wrapperPlatform,
                )}
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="redirect-chevron"
              />
            </summary>
            <div className="redirect-notice-content">
              {t("urlsa.redirect.body").replace(
                "{platform}",
                analysisResult.wrapperPlatform,
              )}
              <div className="redirect-urls">
                <span className="redirect-label">
                  {t("urlsa.redirect.youSubmitted")}
                </span>{" "}
                <span className="redirect-url original">
                  {analysisResult.originalUrl}
                </span>
                <span className="redirect-arrow">→</span>
                <span className="redirect-label">
                  {t("urlsa.redirect.actualDest")}
                </span>{" "}
                <span className="redirect-url resolved">
                  {analysisResult.url}
                </span>
              </div>
            </div>
          </details>
        )}

        <div className="result-content">
          <div
            className={`threat-banner urlsa-threat-banner ${getThreatBannerClass()}`}
          >
            <div className="threat-icon">
              <FontAwesomeIcon icon={getThreatIcon()} />
            </div>
            <h2 className="threat-title">
              {analysisResult?.isScam
                ? t("urlsa.threat.scamDetected")
                : t("urlsa.threat.noThreats")}
            </h2>
            {analysisResult?.isScam === true && (
              <button
                className="threat-banner-share-btn"
                onClick={
                  isIndividualPlan
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        share.toggleShare();
                      }
                }
                disabled={isIndividualPlan}
                title={
                  isIndividualPlan
                    ? t("urlsa.threat.shareIndividual")
                    : t("urlsa.threat.shareFamily")
                }
              >
                <FontAwesomeIcon
                  icon={faShareAlt}
                  className="threat-share-icon"
                />
                <span className="threat-share-label">
                  {t("urlsa.threat.share")}
                </span>
              </button>
            )}
          </div>

          {share.shareOpen && analysisResult?.isScam === true && (
            <InlineShareForm
              shareSuccess={share.shareSuccess}
              shareError={share.shareError}
              shareMessage={share.shareMessage}
              setShareMessage={share.setShareMessage}
              shareLoading={share.shareLoading}
              onCancel={share.closeShare}
              onSend={() => share.sendShare(reportId)}
            />
          )}

          {analysisResult.suggestions && (
            <div className="recommendations">
              <h3 className="section-title">{t("urlsa.results.whatToDo")}</h3>
              <SuggestionsBulletList text={analysisResult.suggestions} />
            </div>
          )}

          <details className="detailed-analysis-collapsible" open>
            <summary className="section-title collapsible-summary">
              {t("urlsa.results.detailedAnalysis")}
              <FontAwesomeIcon
                icon={faChevronDown}
                className="collapsible-chevron"
                style={{ marginLeft: "0.75rem" }}
              />
            </summary>
            <div
              className="summary-text"
              dangerouslySetInnerHTML={{
                __html: analysisResult.explanation,
              }}
            />
          </details>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="sasm-header">
        <div className="sasm-header-top">
          <h1 className="main-title">{t("urlsa.page.title")}</h1>
          <HelpGuideButton guideId={4} setHeroContent={setHeroContent} />
        </div>
        <p className="common-subtitle">{t("urlsa.page.subtitle")}</p>
      </div>

      <div className="urlsa-container" ref={mobileContainerRef}>
        <div
          className={`urlsa-view-slider ${activeSlide === "results" ? "urlsa-slide-results" : ""}`}
        >
          {/* SLIDE 1: Input Form */}
          <div className="urlsa-slide urlsa-slide-input">
            {renderInputForm()}
          </div>

          {/* SLIDE 2: Analysis Results */}
          <div className="urlsa-slide urlsa-slide-detail">
            <button
              type="button"
              className="urlsa-back-btn"
              onClick={showInputSlide}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              {t("urlsa.nav.backToInputs")}
            </button>
            {renderResultsContent()}
          </div>
        </div>
      </div>

      <DisclosuresModal disclaimerText={t("urlsa.disclaimer")} />
    </>
  );
};

export default URLSafetyAnalysisMobile;
